// src/pages/Register.tsx
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Register.css';

const BG_IMAGES = [
  '/assets/img/login-bg/BBVA.jpg',
  '/assets/img/login-bg/BCP.jpg',
  '/assets/img/login-bg/FarmaciasPeruanas.jpg',
  '/assets/img/login-bg/Interbank.jpg',
  '/assets/img/login-bg/Scotiabank.jpg',
];

const Register = () => {
  const [bgIndex, setBgIndex] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [email2, setEmail2]       = useState('');
  const [password, setPassword]   = useState('');
  const [agree, setAgree]         = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  const navigate = useNavigate();

  // Precarga de imágenes para evitar parpadeos
  useEffect(() => {
    BG_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Cambia el fondo cada 10s
  useEffect(() => {
    const id = setInterval(
      () => setBgIndex((i) => (i + 1) % BG_IMAGES.length),
      10000
    );
    return () => clearInterval(id);
  }, []);

  // Validación ligera
  const canSubmit = useMemo(() => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      email.trim().length > 0 &&
      email2.trim().length > 0 &&
      password.length >= 6 &&
      email === email2 &&
      agree &&
      !loading
    );
  }, [firstName, lastName, email, email2, password, agree, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setLoading(true);

    try {
      // Construimos la URL de forma segura (sin barras dobles)
      const base = (import.meta as any).env?.VITE_API_URL || '';
      const url = `${String(base).replace(/\/+$/, '')}/api/auth/register`;

      await axios.post(
        url,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          password,
        },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );

      // Si todo ok, redirige al login
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'There was an error creating your account.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="app" className="app app-full-height app-without-header">
      <div className="register register-with-news-feed">
        {/* Lado izquierdo: imagen grande */}
        <div className="news-feed" aria-hidden="true">
          <div
            className="news-image"
            style={{ backgroundImage: `url(${BG_IMAGES[bgIndex]})` }}
          />
          <div className="news-caption">
            {/* Logo en lugar del h4 */}
            <img
              src="/assets/img/logo/Devioz.png"
              alt="Devioz"
              className="devioz-caption-logo"
            />

            {/* Caption nuevo (título + subtítulo) */}
            <div className="caption-text">
              <div className="caption-title">
                DEVIOZ INNOVACIÓN TECNOLÓGICA PARA UN FUTURO MEJOR.
              </div>
              <div className="caption-subtitle">
                Desarrollamos nuestras aplicaciones web con las últimas investigaciones y mejores prácticas de UX.
              </div>
            </div>
          </div>
        </div>

        {/* Lado derecho: formulario */}
        <div className="register-container">
          {/* Header */}
          <div className="register-header mb-25px h1">
            <div className="mb-1">Sign Up</div>
            <small className="d-block fs-15px lh-16">
              Create your account. It’s free and always will be.
            </small>
          </div>

          {/* Contenido */}
          <div className="register-content">
            <form className="fs-13px" onSubmit={handleSubmit} noValidate>
              {/* Name */}
              <div className="mb-3">
                <label className="mb-2">
                  Name <span className="text-danger">*</span>
                </label>
                <div className="row gx-3">
                  <div className="col-md-6 mb-2 mb-md-0">
                    <input
                      type="text"
                      className="form-control fs-13px"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control fs-13px"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="mb-2">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control fs-13px"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>

              {/* Re-enter Email */}
              <div className="mb-3">
                <label className="mb-2">
                  Re-enter Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control fs-13px"
                  placeholder="Re-enter email address"
                  value={email2}
                  onChange={(e) => setEmail2(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div className="mb-4">
                <label className="mb-2">
                  Password <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className="form-control fs-13px"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
              </div>

              {/* Terms */}
              <div className="form-check mb-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agreementCheckbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="agreementCheckbox">
                  By clicking Sign Up, you agree to our{' '}
                  <a href="#">Terms</a> and that you have read our{' '}
                  <a href="#">Data Policy</a>, including our{' '}
                  <a href="#">Cookie Use</a>.
                </label>
              </div>

              {/* Error */}
              {error && <div className="alert alert-danger">{error}</div>}

              {/* Submit */}
              <div className="mb-4">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn btn-primary d-block w-100 btn-lg h-45px fs-13px"
                >
                  {loading ? 'Creating account...' : 'Sign Up'}
                </button>
              </div>

              {/* Footer form */}
              <div className="mb-4 pb-5">
                Already a member? Click <Link to="/">here</Link> to login.
              </div>

              <hr className="bg-gray-600 opacity-2" />
              <p className="text-center text-gray-600 mb-0">
                &copy; Devioz All Rights Reserved {new Date().getFullYear()}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;